import React, { useState } from 'react';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';
import IconArrowHead from '../../IconArrowHead/IconArrowHead';
import css from './LayoutSideNavigation.module.css';

/**
 * Commonly used layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.mainColumnClassName add more style rules in addition to css.main
 * @param {string?} props.sideNavClassName add more style rules in addition to css.sideNav
 * @param {ReactNode} props.children
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.sideNav
 * @param {ReactNode?} props.footer
 * @param {boolean?} props.useAccountSettingsNav
 * @param {boolean?} props.sideNavDismissable
 * @param {boolean?} props.sideNavInitiallyHidden
 * @param {{ show: string, hide: string }?} props.sideNavToggleAriaLabels
 * @param {string?} props.currentPage
 * @returns {JSX.Element} LayoutComposer that expects children to be a function.
 */
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    accountSettingsNavProps,
    sideNavDismissable,
    sideNavInitiallyHidden = false,
    sideNavToggleAriaLabels,
    ...rest
  } = props;

  const [isSideNavHidden, setIsSideNavHidden] = useState(Boolean(sideNavInitiallyHidden));

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = containerClassName || css.container;

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
            </Topbar>
            <Main as="div" className={containerClasses}>
              <aside
                className={classNames(css.sideNav, sideNavClassName, {
                  [css.sideNavHidden]: sideNavDismissable && isSideNavHidden,
                  [css.sideNavDismissable]: sideNavDismissable,
                })}
              >
                {!isSideNavHidden ? (
                  <>
                    {useAccountSettingsNav ? (
                      <LayoutWrapperAccountSettingsSideNav
                        accountSettingsNavProps={accountSettingsNavProps}
                      />
                    ) : null}
                    {sideNavContent}
                  </>
                ) : null}
                {sideNavDismissable ? (
                  <button
                    type="button"
                    className={classNames(css.sideNavToggleButton, {
                      [css.sideNavToggleButtonHidden]: isSideNavHidden,
                    })}
                    aria-label={
                      isSideNavHidden
                        ? sideNavToggleAriaLabels?.show || 'Show menu'
                        : sideNavToggleAriaLabels?.hide || 'Hide menu'
                    }
                    aria-expanded={!isSideNavHidden}
                    onClick={() => setIsSideNavHidden(prev => !prev)}
                  >
                  {isSideNavHidden ? (
                    <span className={css.sideNavToggleIcon} aria-hidden="true" />
                  ) : (
                    <IconArrowHead className={css.sideNavToggleArrow} direction="left" size="small" />
                  )}
                  </button>
                ) : null}
              </aside>
              <main
                className={classNames(css.main, mainColumnClassName, {
                  [css.mainNoSideNav]: sideNavDismissable && isSideNavHidden,
                })}
              >
                {children}
              </main>
            </Main>
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

export default LayoutSideNavigation;
